/* =============================================================================
   Legere Parts — calculadora de frete (Frenet)

   As cotações são as mesmas que o cliente verá no checkout: quem responde é o
   Carrier Service que o app da Frenet registra na Shopify. Aqui só perguntamos
   pelos endpoints nativos — nenhum token da Frenet chega ao navegador.

   No carrinho:
     POST /cart/prepare_shipping_rates.json  ->  GET /cart/async_shipping_rates.json

   Na página de produto, a Shopify só sabe cotar o carrinho atual, então o modo
   é escolhido nas configurações do tema:
     cart_probe  adiciona a variante, cota e devolve o carrinho ao estado anterior
     cart_only   não cota; manda o cliente ao carrinho
     app_proxy   pergunta a um App Proxy próprio (exige um app; não vem no tema)
   ========================================================================== */
(function () {
  'use strict';

  const T = window.LegereTheme;
  if (!T.shipping || !T.shipping.enabled) return;

  const { formatMoney, fetchJSON } = T.utils;
  const POLL_INTERVAL = 750;
  const POLL_LIMIT = 20;
  const ZIP_KEY = 'legere:zip';

  /* --- CEP ----------------------------------------------------------------- */

  const onlyDigits = (value) => (value || '').replace(/\D/g, '');

  function maskZip(value) {
    const digits = onlyDigits(value).slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  /**
   * O CEP fica salvo no navegador do cliente — assim ele não precisa
   * redigitar em toda página de produto, gaveta ou carrinho. Cada
   * calculadora ainda cota de novo (o frete muda por produto/carrinho), só
   * não pede pra preencher o CEP de novo.
   */
  function savedZip() {
    try {
      return localStorage.getItem(ZIP_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  function saveZip(zip) {
    try {
      localStorage.setItem(ZIP_KEY, zip);
    } catch (_) {
      /* sem espaço ou sem permissão — segue sem salvar */
    }
  }

  /** Resolve cidade/UF pelo ViaCEP. Falha em silêncio: é só um confortozinho. */
  async function lookupZip(zip) {
    if (!T.shipping.viacep) return null;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
      const data = await res.json();
      return data && !data.erro ? data : null;
    } catch (_) {
      return null;
    }
  }

  /* --- Cotação pelos endpoints nativos ------------------------------------- */

  async function requestRates(zip, province) {
    const body = new URLSearchParams();
    body.set('shipping_address[zip]', zip);
    body.set('shipping_address[country]', 'Brazil');
    if (province) body.set('shipping_address[province]', province);

    await fetch(`${T.routes.root}cart/prepare_shipping_rates.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    for (let attempt = 0; attempt < POLL_LIMIT; attempt += 1) {
      const res = await fetch(`${T.routes.root}cart/async_shipping_rates.json`);
      const data = await res.json().catch(() => null);
      // A Shopify devolve `null` enquanto as transportadoras ainda respondem.
      if (data && data.shipping_rates) return data.shipping_rates;
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    }
    throw new Error(T.strings.shippingError);
  }

  /* --- Cotação por sondagem (página de produto) ---------------------------- */

  /**
   * Adiciona a variante, cota e devolve o carrinho exatamente ao que era antes.
   * A restauração fica no `finally` para valer mesmo se a cotação falhar.
   */
  async function quoteWithProbe(variantId, quantity, zip, province) {
    const cart = await fetchJSON(`${T.routes.cart}.js`);
    const existing = cart.items.find((item) => String(item.variant_id) === String(variantId));
    const originalQty = existing ? existing.quantity : 0;

    let lineKey = existing ? existing.key : null;

    try {
      const added = await fetchJSON(T.routes.cartAdd, {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) }] }),
      });
      if (!lineKey && added.items && added.items[0]) lineKey = added.items[0].key;

      return await requestRates(zip, province);
    } finally {
      if (lineKey) {
        // Volta à quantidade original (0 remove a linha que criamos).
        await fetchJSON(T.routes.cartChange, {
          method: 'POST',
          body: JSON.stringify({ id: lineKey, quantity: originalQty }),
        }).catch(() => {
          /* nada mais a fazer aqui; o cliente ainda pode ajustar no carrinho */
        });
      }
    }
  }

  /* --- Cotação por App Proxy ----------------------------------------------- */

  async function quoteWithProxy(variantId, quantity, zip) {
    const url = new URL(T.shipping.proxyPath, window.location.origin);
    url.searchParams.set('cep', zip);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('quantity', quantity);
    const data = await fetchJSON(url.toString());
    return data.shipping_rates || data.rates || [];
  }

  /* --- Renderização -------------------------------------------------------- */

  function rateDeliveryText(rate) {
    const days = rate.delivery_days || [];
    const max = days.length ? Math.max(...days.map(Number)) : null;
    if (!max) return rate.delivery_range ? rate.delivery_range.join(' — ') : '';
    const template = max === 1 ? T.strings.businessDayOne : T.strings.businessDayOther;
    return (template || '[count]').replace('[count]', max);
  }

  /**
   * Cotações viram opções de rádio (a mais barata já marcada) em vez de uma
   * lista só informativa — o cliente escolhe qual frete quer, e no carrinho
   * isso soma no total exibido. É só uma prévia: quem decide de verdade o
   * frete é a Shopify no checkout, mas assim o cliente já sabe quanto vai
   * pagar no total antes de chegar lá.
   */
  function renderRates(root, container, rates) {
    if (!rates.length) {
      container.innerHTML = `<p class="shipping__message">${T.strings.shippingNoRates}</p>`;
      updateTotal(root, null);
      return;
    }

    const groupName = `shipping-rate-${root.dataset.shippingContext}-${Math.random().toString(36).slice(2, 7)}`;
    const rows = rates
      .map((rate, index) => {
        // O preço vem como string decimal na moeda da loja; convertemos p/ centavos.
        const cents = Math.round(parseFloat(rate.price) * 100);
        const price = cents === 0 ? T.strings.shippingFree : formatMoney(cents);
        const delivery = rateDeliveryText(rate);
        return `
          <li class="shipping__rate">
            <label class="shipping__rate-option">
              <input type="radio" name="${groupName}" value="${cents}" data-shipping-rate-input ${index === 0 ? 'checked' : ''}>
              <span class="shipping__rate-info">
                <span class="shipping__rate-name">${escapeHtml(rate.name)}</span>
                ${delivery ? `<span class="shipping__rate-eta">${escapeHtml(delivery)}</span>` : ''}
              </span>
              <span class="shipping__rate-price${cents === 0 ? ' shipping__rate-price--free' : ''}">${price}</span>
            </label>
          </li>`;
      })
      .join('');

    // A opção mais barata já vem marcada, então a lista inteira começa
    // fechada dentro de um <details> — só o resumo (transportadora + preço
    // do frete já selecionado) aparece de cara. Isso é o que sobra de
    // espaço vertical pro produto aparecer na gaveta do carrinho sem rolar;
    // quem quiser trocar de transportadora abre o <details> pra ver as
    // outras opções.
    const cheapest = rates[0];
    const cheapestCents = Math.round(parseFloat(cheapest.price) * 100);
    const cheapestPrice = cheapestCents === 0 ? T.strings.shippingFree : formatMoney(cheapestCents);
    const cheapestDelivery = rateDeliveryText(cheapest);

    container.innerHTML = `
      <details class="shipping__accordion">
        <summary class="shipping__summary">
          <span class="shipping__summary-info">
            <span class="shipping__summary-name">${escapeHtml(cheapest.name)}</span>
            ${cheapestDelivery ? `<span class="shipping__summary-eta">${escapeHtml(cheapestDelivery)}</span>` : ''}
          </span>
          <span class="shipping__summary-price">${cheapestPrice}</span>
        </summary>
        <ul class="shipping__rates" role="list">${rows}</ul>
      </details>`;

    container.querySelectorAll('[data-shipping-rate-input]').forEach((radio, index) => {
      radio.addEventListener('change', () => {
        updateTotal(root, Number(radio.value));
        updateSummary(container, rates[index]);
      });
    });
    updateTotal(root, Number(container.querySelector('[data-shipping-rate-input]').value));
  }

  /**
   * O resumo (transportadora + prazo + preço no cabeçalho do <details>) só
   * era escrito uma vez, sempre com a opção mais barata — trocar de rádio
   * atualizava o total mas não esse texto, então o cliente via "PAC" ali em
   * cima mesmo depois de escolher outra transportadora. Atualiza os três
   * campos pra bater com o rádio marcado.
   */
  function updateSummary(container, rate) {
    const summary = container.querySelector('.shipping__summary');
    if (!summary) return;

    const cents = Math.round(parseFloat(rate.price) * 100);
    const price = cents === 0 ? T.strings.shippingFree : formatMoney(cents);
    const delivery = rateDeliveryText(rate);

    const nameEl = summary.querySelector('.shipping__summary-name');
    const etaEl = summary.querySelector('.shipping__summary-eta');
    const priceEl = summary.querySelector('.shipping__summary-price');

    if (nameEl) nameEl.textContent = rate.name;
    if (priceEl) priceEl.textContent = price;

    if (delivery) {
      if (etaEl) {
        etaEl.textContent = delivery;
      } else {
        const span = document.createElement('span');
        span.className = 'shipping__summary-eta';
        span.textContent = delivery;
        summary.querySelector('.shipping__summary-info').append(span);
      }
    } else if (etaEl) {
      etaEl.remove();
    }
  }

  /** Só nos contextos de carrinho: soma o frete escolhido ao subtotal atual. */
  async function updateTotal(root, shippingCents) {
    const context = root.dataset.shippingContext;
    if (context !== 'cart' && context !== 'cart-drawer') return;

    let totalEl = root.querySelector('[data-shipping-total]');
    if (shippingCents == null) {
      if (totalEl) totalEl.remove();
      return;
    }

    const cart = await fetchJSON(`${T.routes.cart}.js`).catch(() => null);
    if (!cart) return;

    if (!totalEl) {
      totalEl = document.createElement('p');
      totalEl.className = 'shipping__total';
      totalEl.setAttribute('data-shipping-total', '');
      root.querySelector('[data-shipping-results]').after(totalEl);
    }
    totalEl.innerHTML = `<span>${T.strings.shippingEstimatedTotal}</span><strong>${formatMoney(
      cart.total_price + shippingCents
    )}</strong>`;
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  /* --- Componente ---------------------------------------------------------- */

  function initCalculator(root) {
    if (root.dataset.shippingReady) return;
    root.dataset.shippingReady = 'true';

    const input = root.querySelector('[data-shipping-zip]');
    const button = root.querySelector('[data-shipping-submit]');
    const results = root.querySelector('[data-shipping-results]');
    const context = root.dataset.shippingContext || 'cart';
    if (!input || !button || !results) return;

    input.addEventListener('input', () => {
      input.value = maskZip(input.value);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        button.click();
      }
    });

    async function calculate() {
      const zip = onlyDigits(input.value);
      if (zip.length !== 8) {
        results.innerHTML = `<p class="shipping__message shipping__message--error">${T.strings.zipInvalid}</p>`;
        input.focus();
        return;
      }

      const label = button.textContent;
      button.disabled = true;
      button.textContent = T.strings.shippingCalculating || '...';
      results.innerHTML = '';

      try {
        const address = await lookupZip(zip);
        if (address && T.strings.shippingDeliveryTo) {
          results.innerHTML = `<p class="shipping__message">${escapeHtml(
            T.strings.shippingDeliveryTo.replace('[city]', `${address.localidade} — ${address.uf}`)
          )}</p>`;
        }

        let rates;
        if (context === 'product') {
          const variantId = root.dataset.variantId;
          const qtyInput = document.querySelector('[data-product-qty]');
          const quantity = qtyInput ? Math.max(1, Number(qtyInput.value)) : 1;

          if (T.shipping.pdpMode === 'app_proxy') {
            rates = await quoteWithProxy(variantId, quantity, zip);
          } else {
            rates = await quoteWithProbe(variantId, quantity, zip, address ? address.uf : null);
          }
        } else {
          rates = await requestRates(zip, address ? address.uf : null);
        }

        renderRates(root, results, rates);
        saveZip(zip);
      } catch (error) {
        results.innerHTML = `<p class="shipping__message shipping__message--error">${escapeHtml(
          error.message || T.strings.shippingError
        )}</p>`;
      } finally {
        button.disabled = false;
        button.textContent = label;
      }
    }

    button.addEventListener('click', calculate);

    // CEP em buffer: já vem preenchido e cotado, sem o cliente digitar de
    // novo em cada produto/carrinho — só o resultado muda (frete depende do
    // que está sendo cotado), o CEP em si o cliente já informou uma vez.
    const remembered = savedZip();
    if (remembered && !input.value) {
      input.value = maskZip(remembered);
      calculate();
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-shipping-calculator]').forEach(initCalculator);
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('cart:updated', () => initAll(document));

  // Mantém o id da variante em dia quando o cliente troca de opção.
  document.addEventListener('variant:changed', (event) => {
    document.querySelectorAll('[data-shipping-context="product"]').forEach((root) => {
      root.dataset.variantId = event.detail.variant.id;
    });
  });
})();
